/**
 * A trainer's tags on the library page: what the editor offers, what a press of Enter writes, and
 * what a rename is about to do (exercise-library R21 to R23).
 *
 * The SERVER is the authority — it folds, reuses the first spelling, merges and refuses — and every
 * write here sends a whole set and redraws from the answer. What this module decides is what the
 * trainer is told BEFORE that answer: that a tag is already on the movement, that the tenth is the
 * last, that a rename onto a name they already use is a merge, and of how many movements.
 *
 * A tag's identity is its normalised form (R22), the same normalisation the search uses, so
 * „Kolano" and „kolano " are one tag here exactly as they are one tag on the server.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  normaliseExerciseName,
  searchWords,
  type ExerciseListItem,
} from '~/entities/exercise-library';

/** R22's three limits. The server refuses past each; these let the editor say so first. */
export const MAX_TAGS_PER_EXERCISE = 10;
export const MAX_TAGS_PER_TRAINER = 200;
export const MAX_TAG_LENGTH = 32;

/** Why a tag cannot be written. The server's three codes, and the editor's own reading of them. */
export type TagRefusal = 'invalid' | 'perExercise' | 'perTrainer';

/** One of the trainer's tags as the server lists it: `GET /exercise/tags`. */
export interface TagUsage {
  readonly label: string;
  readonly exerciseCount: number;
}

/** How many suggestions the editor lists under the field. */
const SUGGESTION_LIMIT = 6;

/** One line under the tag field: a tag the trainer already uses, or the typed text as a new one. */
export type TagSuggestion =
  | { readonly kind: 'existing'; readonly key: string; readonly label: string; readonly count: number }
  | { readonly kind: 'create'; readonly key: string; readonly label: string };

const keysOf = (labels: readonly string[]) => new Set(labels.map(normaliseExerciseName));

/** A typed label the server would accept: trimmed, one to thirty-two characters, not punctuation alone. */
function acceptedLabel(typed: string): string | null {
  const label = typed.trim();
  if (label.length === 0 || label.length > MAX_TAG_LENGTH) return null;
  return normaliseExerciseName(label) === '' ? null : label;
}

/**
 * What the editor lists for what has been typed.
 *
 * The trainer's own tags whose label holds every typed word, most used first, leaving out the ones
 * already on this movement — and the typed text itself as a new tag, LAST, when it is acceptable and
 * is not already one of theirs. Nothing typed lists nothing: the field is for adding, and an open
 * list of everything they own is the filter bar's job.
 */
export function tagSuggestions(
  typed: string,
  onExercise: readonly string[],
  ownTags: readonly TagUsage[],
): TagSuggestion[] {
  const words = searchWords(typed);
  if (words.length === 0) return [];
  const taken = keysOf(onExercise);
  const keyed = ownTags.map((tag) => ({ ...tag, key: normaliseExerciseName(tag.label) }));

  const existing: TagSuggestion[] = keyed
    .filter((tag) => !taken.has(tag.key) && words.every((word) => tag.key.includes(word)))
    .sort((left, right) => right.exerciseCount - left.exerciseCount)
    .slice(0, SUGGESTION_LIMIT)
    .map((tag) => ({ kind: 'existing', key: tag.key, label: tag.label, count: tag.exerciseCount }));

  const label = acceptedLabel(typed);
  const key = label === null ? '' : normaliseExerciseName(label);
  const known = taken.has(key) || keyed.some((tag) => tag.key === key);
  return label === null || known ? existing : [...existing, { kind: 'create', key, label }];
}

/** What pressing Enter on a label does to this movement's set. */
export type TagAddition =
  | { readonly kind: 'add'; readonly labels: readonly string[] }
  /** Nothing typed but spaces: Enter on an empty field is not an attempt, so nothing is said. */
  | { readonly kind: 'nothing' }
  | { readonly kind: 'already' }
  | { readonly kind: 'refused'; readonly refusal: TagRefusal };

/**
 * Add one label to a movement's tags.
 *
 * A variant of a tag the trainer already uses is written in THAT tag's spelling (R22: the first
 * spelling written is the one shown) — the server would reuse it anyway, and sending it means the
 * chip that appears is the one the trainer will see after the answer. A label already on the
 * movement is not an error and writes nothing.
 */
export function addTag(
  onExercise: readonly string[],
  typed: string,
  ownTags: readonly TagUsage[],
): TagAddition {
  if (typed.trim() === '') return { kind: 'nothing' };
  const label = acceptedLabel(typed);
  if (label === null) return { kind: 'refused', refusal: 'invalid' };
  const key = normaliseExerciseName(label);
  if (keysOf(onExercise).has(key)) return { kind: 'already' };
  if (onExercise.length >= MAX_TAGS_PER_EXERCISE) return { kind: 'refused', refusal: 'perExercise' };

  const known = ownTags.find((tag) => normaliseExerciseName(tag.label) === key);
  if (known === undefined && ownTags.length >= MAX_TAGS_PER_TRAINER) {
    return { kind: 'refused', refusal: 'perTrainer' };
  }
  return { kind: 'add', labels: [...onExercise, known?.label ?? label] };
}

/** The movement's set without one tag, matched by identity rather than by spelling. */
export function removeTag(onExercise: readonly string[], label: string): readonly string[] {
  const key = normaliseExerciseName(label);
  return onExercise.filter((candidate) => normaliseExerciseName(candidate) !== key);
}

/** What saving a rename is about to do. */
export type TagRename =
  | { readonly kind: 'invalid' }
  /** The very spelling it already has. Saving changes nothing. */
  | { readonly kind: 'unchanged' }
  /** A new name, or the same name in a new spelling. */
  | { readonly kind: 'rename'; readonly label: string }
  /**
   * A name the trainer already uses for ANOTHER tag: the two become one, in the typed spelling
   * (R23), on `count` movements — a movement that carried both carries it once.
   */
  | {
      readonly kind: 'merge';
      readonly label: string;
      readonly target: string;
      readonly count: number;
    };

/**
 * Read a rename before it is saved.
 *
 * The merged count is taken from the catalogue in the browser, because it is the one figure the
 * usage list cannot give: it holds each tag's own count, and the overlap between two tags is
 * exactly what a merge removes.
 */
export function tagRenamePlan(
  from: string,
  typed: string,
  usage: readonly TagUsage[],
  items: readonly ExerciseListItem[],
): TagRename {
  const label = acceptedLabel(typed);
  if (label === null) return { kind: 'invalid' };
  if (label === from.trim()) return { kind: 'unchanged' };

  const fromKey = normaliseExerciseName(from);
  const toKey = normaliseExerciseName(label);
  const target = toKey === fromKey ? undefined : usage.find(
    (tag) => normaliseExerciseName(tag.label) === toKey,
  );
  if (target === undefined) return { kind: 'rename', label };

  const count = items.filter((item) =>
    item.tags.some((tag) => {
      const key = normaliseExerciseName(tag);
      return key === fromKey || key === toKey;
    }),
  ).length;
  return { kind: 'merge', label, target: target.label, count };
}

/** The manage dialog's rows: by label, in the reader's collation, so „Łydki" sorts after „Lędźwie". */
export function orderedTagUsage(usage: readonly TagUsage[], locale: string): TagUsage[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  return [...usage].sort((left, right) => collator.compare(left.label, right.label));
}

/**
 * Where focus goes when one entry of a list of tags is removed by its own control: the entry after
 * it, else the one before, else `null` — the caller's fallback (the field, the dialog's close). A
 * control that unmounts under focus drops it to the page behind, and a keyboard user then has to
 * find the list again.
 */
export function tagFocusedAfterRemoving(labels: readonly string[], removed: string): string | null {
  const at = labels.indexOf(removed);
  if (at < 0) return null;
  return labels[at + 1] ?? labels[at - 1] ?? null;
}
